import type { ApexOptions } from 'apexcharts'
import type { SectionId, SectionMetrics } from '../../../features/engagement/domain/models/sections'
import {
  buildStackedBarChartOptions,
  compactNumberFormatter,
  localeNumberFormatter,
  type ChartSeriesItem,
  type LegendState
} from '../../shared/charts'
import type { LegendItem } from '../../shared/charts/components/ChartLegend'
import { dayFormatter } from '../../shared/charts/config/chart-formatters'
import { formatDurationHours, SECTION_COLORS, SECTION_LABEL_KEYS, type TranslateFn } from './section-display'

export type SectionsMetricKey = 'seconds' | 'visits' | 'interactions'

// Shared by the stacked chart and the tier comparison so both metric pickers stay in step
export const SECTIONS_METRIC_OPTIONS: { value: SectionsMetricKey; labelKey: string }[] = [
  { value: 'seconds', labelKey: 'ls_sections_time' },
  { value: 'visits', labelKey: 'ls_sections_visits' },
  { value: 'interactions', labelKey: 'ls_sections_interactions' }
]

// 'other' is deliberately excluded from the stacked view (spec §6, "(excl. Other)")
export const STACKED_SECTION_IDS: Exclude<SectionId, 'other'>[] = [
  'home',
  'community',
  'chat',
  'songs',
  'videos',
  'events',
  'livestream',
  'shop'
]

export const SECTIONS_STACKED_INITIAL_LEGEND_STATE: LegendState = Object.fromEntries(
  STACKED_SECTION_IDS.map((section) => [section, true])
)

export const getSectionsStackedLegendItems = (t: TranslateFn): LegendItem[] =>
  STACKED_SECTION_IDS.map((section) => ({
    key: section,
    label: t(SECTION_LABEL_KEYS[section]),
    color: SECTION_COLORS[section]
  }))

export const getSectionsStackedChartOptions = (
  sections: SectionMetrics[],
  metric: SectionsMetricKey,
  legend: LegendState,
  t: TranslateFn
): ApexOptions => {
  const stackable = sections.filter((s) => s.section !== 'other')
  const visible = stackable.filter((s) => legend[s.section])

  const categories = (visible[0] ?? stackable[0])?.periods.map((p) => p.date) ?? []

  const series: ChartSeriesItem[] = visible.map((s) => ({
    name: t(SECTION_LABEL_KEYS[s.section]),
    data: s.periods.map((p) => p[metric]),
    color: SECTION_COLORS[s.section]
  }))

  const isTime = metric === 'seconds'

  return buildStackedBarChartOptions({
    series,
    categories,
    yAxisFormatter: isTime ? formatDurationHours : compactNumberFormatter,
    tooltipValueFormatter: isTime ? formatDurationHours : localeNumberFormatter,
    // Period dates are "YYYY-MM-DD" strings (daily/weekly/monthly bucket starts)
    xAxisLabelFormatter: dayFormatter
  })
}
